// Il mio feed, connected: the panel of the folders on the left, the two
// lists of the chosen one on the right. The first folder, «Preferiti», is
// the pinned sources themselves (GET /feed?favourites=true); the others are
// the user's feeds (GET /feed?feed_id=). The chosen folder is ?feed= in the
// query string: `preferiti`, or absent, is «Preferiti», which also stands
// in for an id that is not the user's (a deleted feed, a hand-written id).
// Before a feed can be shown (the feeds loading or failed, a feed without
// sources) the two sections say so; a failed refetch leaves the feeds
// already read on the page (the panel says so).
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { NewsLayout } from "../../components/NewsLayout";
import { NewsSection } from "../../components/news/NewsSection";
import { PanelStatus } from "../../components/PanelStatus";
import { favouriteKeys, lastAccessLabel } from "../news/adapt";
import { FAVOURITES_SCOPE, useSources, useVisit } from "../news/api";
import { LatestNews } from "../news/LatestNews";
import { ListLoading } from "../news/ListStatus";
import { readNewsParams, withNewsParams } from "../news/params";
import { FAVOURITES_FOLDER, selectedFeed, sortFeeds } from "./adapt";
import { useFeeds } from "./api";
import { FEEDS_TITLE, FeedsPanel } from "./FeedsPanel";

const TITLE_NEW = "News dall’ultimo accesso";
const TITLE_PREVIOUS = "News precedenti";
const LOADING = "Caricamento del feed…";
const FAILED = "Feed non disponibili.";
const NO_FAVOURITES = "Nessuna fonte preferita: usa il pin in Ultime news.";
const EMPTY_FEED = "Il feed non ha fonti: aggiungile con «Modifica» nel pannello.";

export function MyFeed() {
  const [search, setSearch] = useSearchParams();
  const feedsQuery = useFeeds();
  const catalogue = useSources();
  const visit = useVisit();
  const feeds = useMemo(() => (feedsQuery.data ? sortFeeds(feedsQuery.data.feeds) : null), [feedsQuery.data]);
  const favourites = useMemo(() => (catalogue.data ? favouriteKeys(catalogue.data) : null), [catalogue.data]);
  const fromUrl = readNewsParams(search).feed;
  // «Preferiti» needs no feed: only a feed named in the URL waits for the feeds
  const wantsFeed = fromUrl !== null && fromUrl !== FAVOURITES_FOLDER;
  const selected = feeds ? selectedFeed(feeds, fromUrl) : null;
  // a feed holds favourites only: a source unpinned since the feeds were read does not count
  const sources = selected
    ? favourites
      ? selected.sources.filter((k) => favourites.includes(k))
      : selected.sources
    : [];

  // the query string as it is now, for the answer of a call sent while another one was on the page:
  // the setter of useSearchParams, even in its functional form, starts from the query string of its own render
  const latestSearch = useRef(search);
  useEffect(() => {
    latestSearch.current = search;
  }, [search]);

  const onSelect = (id: string) => setSearch(withNewsParams(latestSearch.current, { tab: "feed", feed: id }));
  // a deleted feed leaves the URL only if it is still the chosen one (the user may have chosen another meanwhile)
  const onDeleted = (id: string) => {
    const now = latestSearch.current;
    if (readNewsParams(now).feed === id) setSearch(withNewsParams(now, { tab: "feed", feed: null }));
  };
  const dateLabel = visit.data ? lastAccessLabel(visit.data) : undefined;

  // a failed call being retried («Riprova») is loading again, not still failed; with the feeds already
  // read, a failed refetch keeps them on the page: the panel says so in one line
  const failed = feedsQuery.isError && !feedsQuery.isFetching;
  let content: React.ReactNode;
  if (wantsFeed && feedsQuery.data === undefined)
    content = failed ? (
      <Notice dateLabel={dateLabel} role="alert" onRetry={() => void feedsQuery.refetch()}>
        {FAILED}
      </Notice>
    ) : (
      <Notice dateLabel={dateLabel} loading />
    );
  else if (!selected)
    content =
      favourites && favourites.length === 0 ? (
        <Notice dateLabel={dateLabel}>{NO_FAVOURITES}</Notice>
      ) : (
        <LatestNews scope={FAVOURITES_SCOPE} />
      );
  else if (sources.length === 0) content = <Notice dateLabel={dateLabel}>{EMPTY_FEED}</Notice>;
  else content = <LatestNews scope={{ feedId: selected.id }} />;

  return (
    <NewsLayout
      sidebar={{
        title: FEEDS_TITLE,
        render: (collapse) => (
          <FeedsPanel
            selectedId={selected?.id ?? null}
            wantsFeed={wantsFeed}
            onSelect={onSelect}
            onDeleted={onDeleted}
            onCollapse={collapse}
          />
        ),
      }}
    >
      {content}
    </NewsLayout>
  );
}

type NoticeProps = {
  dateLabel?: string;
  loading?: boolean;
  role?: "status" | "alert";
  onRetry?: () => void;
  children?: React.ReactNode;
};

/** The sections of a notice hold no card, so there is no bookmark to toggle. */
const noCards = () => {};

/** The two sections with a status row (or the loading placeholders) instead of the cards. */
function Notice({ dateLabel, loading, role, onRetry, children }: NoticeProps) {
  // one status row per section: the same element cannot stand in two places
  const status = () =>
    loading ? (
      <ListLoading label={LOADING} />
    ) : (
      <PanelStatus role={role} indent={false} onRetry={onRetry}>
        {children}
      </PanelStatus>
    );
  return (
    <>
      <NewsSection title={TITLE_NEW} dateLabel={dateLabel} items={[]} onToggleSaved={noCards}>
        {status()}
      </NewsSection>
      <NewsSection title={TITLE_PREVIOUS} items={[]} onToggleSaved={noCards}>
        {status()}
      </NewsSection>
    </>
  );
}
